import React, { useEffect, useMemo, useState } from 'react';
import { Form } from '@/components/ui';
import { VENDOR_COMPLIANCE_SECTION } from '../vendorCompliance';

interface ReportSidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  canCreateD2: boolean;
  canCreateD3: boolean;
  canCreateWildlife?: boolean;
  canCreateRockfall?: boolean;
  canCreateSigns?: boolean;
  canCreateCulverts?: boolean;
  canCreateCattleguard?: boolean;
  canCreateEquipmentUsage?: boolean;
  canCreateMaterialUsage?: boolean;
  canCreateOverdueTasks?: boolean;
  canCreateLabourSubmittedTime?: boolean;
  canCreateQBS?: boolean;
  canCreateWeeklyPayroll?: boolean;
  canCreatePayrollVariance?: boolean;
  canViewFinanceMonthEnd?: boolean;
  canViewReports?: boolean;
  /** EQ-30: View Repairs opens the Equipment category (Open Defects). */
  canViewRepairs?: boolean;
  /** V2-078: the Vendor Compliance widget or vendor view permission. */
  canViewVendorCompliance?: boolean;
  isMaterialControllerOnly?: boolean; // True if user only has material_usage_create permission (not report:view)
}

interface ReportItem {
  id: string;
  label: string;
  show: boolean;
}

interface ReportCategory {
  id: string;
  label: string;
  icon: string;
  items: ReportItem[];
  show?: boolean;
}

export const EXPORT_HISTORY_ID = 'export-history';

/** The category a section id belongs to ("mott-d2" → "mott"), or null for Export History / none. */
export function categoryOf(section: string): string | null {
  const m = /^(mott|finance|payroll|operations|equipment|procurement)(-|$)/.exec(section);
  return m ? m[1] : null;
}

// Layout only - text colour is set per row (reports vs category headers), since two tw-text-*
// colours on one element resolve by stylesheet order, not class order.
const rowClass =
  'tw-flex tw-w-full tw-min-h-[44px] tw-items-center tw-rounded-md tw-border-0 tw-bg-transparent tw-px-2 tw-py-2 tw-text-left tw-text-sm [@media(hover:hover)]:hover:tw-bg-surface-hover';
const itemClass = `${rowClass} tw-text-content`;

/**
 * Reports navigation (W-M14).
 *
 * - Categories are independent disclosures: opening one never closes another and never clears
 *   the open report.
 * - The open report is marked with `.wrap-current-page` (app-wide "you are here") and keeps the
 *   same left alignment and indent as its siblings. Its category shows only as expanded
 *   (chevron up) - no fill.
 * - Export History uses the same selected style and is listed last.
 */
const ReportSidebar: React.FC<ReportSidebarProps> = ({
  activeSection,
  setActiveSection,
  canCreateD2,
  canCreateD3,
  canCreateWildlife = true,
  canCreateRockfall = true,
  canCreateSigns = true,
  canCreateCulverts = true,
  canCreateCattleguard = true,
  canCreateEquipmentUsage = true,
  canCreateMaterialUsage = true,
  canCreateOverdueTasks = true,
  canCreateLabourSubmittedTime = false,
  canCreateQBS = false,
  canCreateWeeklyPayroll = false,
  canCreatePayrollVariance = false,
  canViewFinanceMonthEnd = false,
  canViewReports = true,
  canViewRepairs = false,
  canViewVendorCompliance = false,
  isMaterialControllerOnly = false
}) => {
  const categories: ReportCategory[] = useMemo(
    () => [
      {
        id: 'mott',
        label: 'MOTT Reports',
        icon: 'bi-file-earmark-text',
        items: [
          { id: 'mott-d2', label: 'D2', show: canCreateD2 },
          { id: 'mott-d3', label: 'D3', show: canCreateD3 },
          { id: 'mott-chris-signs', label: 'CHRIS – Signs', show: canCreateSigns },
          { id: 'mott-chris-culverts', label: 'CHRIS – Culverts', show: canCreateCulverts },
          { id: 'mott-chris-cattleguards', label: 'CHRIS – Cattleguards', show: canCreateCattleguard },
          { id: 'mott-wildlife', label: 'Wildlife', show: true },
          { id: 'mott-rockfall', label: 'Rockfall', show: canCreateRockfall },
        ],
      },
      {
        id: 'finance',
        label: 'Finance Reports',
        icon: 'bi-currency-dollar',
        items: [
          { id: 'finance-monthend', label: 'Month-End Report', show: canViewFinanceMonthEnd },
          { id: 'finance-preliminary-job-cost', label: 'Preliminary Job Cost', show: true },
        ],
      },
      {
        id: 'payroll',
        label: 'Payroll Reports',
        icon: 'bi-cash-coin',
        show: canCreateWeeklyPayroll || canCreatePayrollVariance,
        items: [
          { id: 'payroll-weekly', label: 'Payroll Report', show: canCreateWeeklyPayroll },
          { id: 'payroll-weekly-overtime', label: 'Overtime Report', show: canCreateWeeklyPayroll },
          { id: 'payroll-variance', label: 'Payroll Variance Report', show: canCreatePayrollVariance },
        ],
      },
      {
        id: 'operations',
        label: 'Operations Reports',
        icon: 'bi-truck',
        items: [
          { id: 'operations-overdue-tasks', label: 'Overdue Tasks', show: canCreateOverdueTasks },
          { id: 'operations-material-usage', label: 'Material Usage', show: canCreateMaterialUsage },
          { id: 'operations-labour', label: 'Submitted Time', show: canCreateLabourSubmittedTime && canViewReports },
          { id: 'operations-qbs-summary', label: 'QBS Summary', show: canCreateQBS },
          { id: 'operations-qbs-project-summary', label: 'Project Summary', show: canCreateQBS },
          { id: 'operations-equipment-usage', label: 'Equipment Usage', show: canCreateEquipmentUsage },
          { id: 'operations-quantified-costing', label: 'Quantified Costing', show: canCreateQBS },
          { id: 'operations-quantified-value', label: 'Quantified Value', show: canCreateQBS },
        ],
      },
      {
        id: 'equipment',
        label: 'Equipment',
        icon: 'bi-wrench-adjustable',
        show: canViewRepairs,
        items: [{ id: 'equipment-open-defects', label: 'Open Defects', show: canViewRepairs }],
      },
      // V2-078: a new category for procurement reports.
      {
        id: 'procurement',
        label: 'Procurement Reports',
        icon: 'bi-shield-check',
        show: canViewVendorCompliance,
        items: [{ id: VENDOR_COMPLIANCE_SECTION, label: 'Vendor Compliance', show: canViewVendorCompliance }],
      },
    ],
    [
      canCreateD2, canCreateD3, canCreateSigns, canCreateCulverts, canCreateCattleguard, canCreateWildlife,
      canCreateRockfall, canViewFinanceMonthEnd, canCreateEquipmentUsage, canCreateQBS, canCreateWeeklyPayroll,
      canCreatePayrollVariance, canCreateOverdueTasks, canCreateMaterialUsage, canCreateLabourSubmittedTime,
      canViewReports, canViewRepairs, canViewVendorCompliance,
    ],
  );

  const [openCategories, setOpenCategories] = useState<Set<string>>(() => {
    const c = categoryOf(activeSection);
    return new Set(c ? [c] : []);
  });

  // A report opened from elsewhere (the phone picker, a deep link) opens its category too.
  useEffect(() => {
    const c = categoryOf(activeSection);
    if (!c) return;
    setOpenCategories((prev) => (prev.has(c) ? prev : new Set(prev).add(c)));
  }, [activeSection]);

  const toggleCategory = (id: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderItem = (item: { id: string; label: React.ReactNode }, icon?: string) => {
    const selected = activeSection === item.id;
    return (
      <li key={item.id}>
        <button
          type="button"
          className={`${itemClass} ${selected ? 'wrap-current-page' : ''}`}
          aria-current={selected ? 'page' : undefined}
          onClick={() => setActiveSection(item.id)}
        >
          {icon && <i className={`bi ${icon} tw-mr-2`} aria-hidden="true"></i>}
          <span className="tw-min-w-0 tw-flex-1">{item.label}</span>
        </button>
      </li>
    );
  };

  // Without report:view: Material Usage, plus Vendor Compliance for View Vendors holders
  if (isMaterialControllerOnly) {
    return (
      // h-full instead of minHeight: 100vh - the reporting shell is already a bounded flex pane
      // (see ReportingView), so 100vh double-counted the navbar/page chrome and pushed the report
      // content off screen on a phone ("Reports not visible at all in mobile view").
      <div className="tw-bg-surface-alt tw-border-r tw-border-line report-sidebar tw-h-full tw-overflow-y-auto">
        <nav className="tw-p-2" aria-label="Reports">
          <h5 className="tw-mb-3">Reports</h5>
          <ul className="tw-m-0 tw-list-none tw-space-y-0.5 tw-p-0">
            {canCreateMaterialUsage && renderItem({ id: 'operations-material-usage', label: 'Material Usage' })}
            {/* V3-006 (DQ5): every holder of View Vendors sees Procurement > Vendor Compliance. */}
            {canViewVendorCompliance && renderItem({ id: VENDOR_COMPLIANCE_SECTION, label: 'Vendor Compliance' })}
          </ul>
        </nav>
      </div>
    );
  }

  const activeCategory = categoryOf(activeSection);
  const visibleCategories = categories
    .filter((c) => c.show !== false)
    .map((c) => ({ ...c, items: c.items.filter((i) => i.show) }));

  return (
    // h-full + its own overflow-y-auto instead of minHeight: 100vh: the reporting shell already
    // bounds this pane's height (see ReportingView), so 100vh made the sidebar taller than the
    // visible area and pushed the actual report off screen - "Reports not visible at all in
    // mobile view". flex-col so the desktop nav tree (below) can scroll independently of the
    // mobile picker above it.
    <div className="tw-bg-surface-alt tw-border-r tw-border-line report-sidebar tw-h-full tw-flex tw-flex-col tw-overflow-hidden">
      {/* Below md there's no room for a full-height list of every report next to the report
          itself, so collapse the nav tree to a single <select> here and hide the tree (below). */}
      <div className="tw-flex-shrink-0 tw-border-b tw-border-line tw-p-2 md:tw-hidden">
        <Form.Group controlId="report-sidebar-picker" className="tw-mb-0">
          <Form.Label className="tw-text-sm tw-mb-1">Report</Form.Label>
          <Form.Select
            size="sm"
            value={activeSection}
            onChange={(e) => setActiveSection(e.target.value)}
          >
            <option value="">Select a report…</option>
            {visibleCategories.map((c) => (
              <optgroup key={c.id} label={c.label}>
                {c.items.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label}
                  </option>
                ))}
              </optgroup>
            ))}
            <option value={EXPORT_HISTORY_ID}>Export History</option>
          </Form.Select>
        </Form.Group>
      </div>
      <nav className="tw-hidden tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-2 md:tw-block" aria-label="Reports">
        <h5 className="tw-mb-3">Reports</h5>
        <ul className="tw-m-0 tw-list-none tw-space-y-0.5 tw-p-0">
          {visibleCategories.map((c) => {
            const open = openCategories.has(c.id);
            const listId = `report-category-${c.id}`;
            // R-06: a category header must not read like one of its reports - headers are semibold
            // ink. V3-077 / NAV-07: every header keeps that normal colour; only the selected report
            // carries the current-page style. The open category shows only as expanded.
            const holdsCurrent = activeCategory === c.id;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  className={`${rowClass} tw-justify-between tw-font-semibold tw-text-ink`}
                  aria-expanded={open}
                  aria-controls={listId}
                  data-current-category={holdsCurrent || undefined}
                  onClick={() => toggleCategory(c.id)}
                >
                  <span className="tw-flex tw-min-w-0 tw-items-center">
                    <i className={`bi ${c.icon} tw-mr-2`} aria-hidden="true"></i>
                    <span>{c.label}</span>
                  </span>
                  <i className={`bi tw-ml-2 ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true"></i>
                </button>
                {open && (
                  // ~12px indent, every report left-aligned like its siblings.
                  <ul id={listId} className="tw-m-0 tw-list-none tw-space-y-0.5 tw-p-0 tw-pl-3">
                    {c.items.map((i) => renderItem(i))}
                  </ul>
                )}
              </li>
            );
          })}
          {/* Listed last: it is not a report, it is what was exported from them. */}
          {renderItem({ id: EXPORT_HISTORY_ID, label: 'Export History' }, 'bi-clock-history')}
        </ul>
      </nav>
    </div>
  );
};

export default ReportSidebar;
